import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { 
  wechatLogin, 
  emailLogin, 
  emailRegister, 
  setShowLoginModal, 
  setShowRegisterModal,
  setLoginMode,
  getFavorites
} from '../store/authSlice';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.95);
  border-radius: 16px;
  padding: 32px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  margin-bottom: 24px;
  font-size: 1.8rem;
  font-weight: 600;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const TabButton = styled.button`
  flex: 1;
  background: none;
  border: none;
  color: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.6)'};
  padding: 12px 16px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid ${props => props.active ? '#667eea' : 'transparent'};
  
  &:hover {
    color: #ffffff;
  }
`;

const FormContainer = styled.form`
  text-align: left;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const InputLabel = styled.label`
  display: block;
  color: #ffffff;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: #ffffff;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    background: #555;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  &::before {
    margin-right: 16px;
  }
  
  &::after {
    margin-left: 16px;
  }
`;

const WeChatButton = styled.button`
  width: 100%;
  background: #07c160;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(7, 193, 96, 0.3);
  
  &:hover {
    background: #06ad56;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(7, 193, 96, 0.4);
  }
  
  &:disabled {
    background: #555;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  margin-bottom: 20px;
  font-size: 14px;
`;

const WeChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.504a.59.59 0 0 0 .634.736l1.939-.38a.59.59 0 0 1 .436.068 8.1 8.1 0 0 0 2.857.523c.276 0 .548-.014.815-.041-.853-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.353-8.6-6.353zM5.785 5.991c.642 0 1.162.52 1.162 1.162s-.52 1.162-1.162 1.162-1.162-.52-1.162-1.162.52-1.162 1.162-1.162zm5.813 0c.642 0 1.162.52 1.162 1.162s-.52 1.162-1.162 1.162-1.162-.52-1.162-1.162.52-1.162 1.162-1.162zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.476.476 0 0 1 .352.054l1.513.314a.476.476 0 0 0 .508-.595l-.33-1.22a.476.476 0 0 1 .165-.506c1.542-1.176 2.524-2.837 2.524-4.688 0-3.387-3.305-6.165-7.517-6.258zm-2.23 2.242c.534 0 .967.433.967.967s-.433.967-.967.967-.967-.433-.967-.967.433-.967.967-.967zm4.837 0c.534 0 .967.433.967.967s-.433.967-.967.967-.967-.433-.967-.967.433-.967.967-.967z"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const LoginModal = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { showLoginModal, showRegisterModal, loading, error, isLoginMode } = useSelector(state => state.auth);

  const isActive = showLoginModal || showRegisterModal;
  
  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (isLoginMode) {
        // 登录
        await dispatch(emailLogin({
          email: formData.email,
          password: formData.password
        })).unwrap();
        // 登录成功后获取收藏列表
        await dispatch(getFavorites()).unwrap();
      } else {
        // 注册
        if (formData.password !== formData.confirmPassword) {
          alert('两次输入的密码不一致');
          return;
        }
        
        await dispatch(emailRegister({
          username: formData.username,
          email: formData.email,
          password: formData.password
        })).unwrap();
        // 注册成功后获取收藏列表
        await dispatch(getFavorites()).unwrap();
      }
    } catch (error) {
      console.error('操作失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWechatLogin = () => {
    // 获取微信授权URL
    const appId = process.env.REACT_APP_WECHAT_APPID || 'your_app_id';
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = 'snsapi_userinfo';
    const state = 'wechat_login';
    
    const wechatAuthUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`;
    
    // 跳转到微信授权页面
    window.location.href = wechatAuthUrl;
  };

  const handleClose = () => {
    dispatch(setShowLoginModal(false));
    dispatch(setShowRegisterModal(false));
  };

  const switchMode = (loginMode) => {
    dispatch(setLoginMode(loginMode));
    // 清空表单数据
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      username: ''
    });
  };

  if (!isActive) {
    return null;
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{isLoginMode ? '登录' : '注册'}</ModalTitle>
        
        <TabContainer>
          <TabButton 
            active={isLoginMode} 
            onClick={() => switchMode(true)}
          >
            登录
          </TabButton>
          <TabButton 
            active={!isLoginMode} 
            onClick={() => switchMode(false)}
          >
            注册
          </TabButton>
        </TabContainer>
        
        <FormContainer onSubmit={handleEmailSubmit}>
          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}
          
          {!isLoginMode && (
            <InputGroup>
              <InputLabel>用户名</InputLabel>
              <Input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="请输入用户名"
                required
              />
            </InputGroup>
          )}
          
          <InputGroup>
            <InputLabel>邮箱</InputLabel>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="请输入邮箱地址"
              required
            />
          </InputGroup>
          
          <InputGroup>
            <InputLabel>密码</InputLabel>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="请输入密码"
              required
              minLength="6"
            />
          </InputGroup>
          
          {!isLoginMode && (
            <InputGroup>
              <InputLabel>确认密码</InputLabel>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="请再次输入密码"
                required
                minLength="6"
              />
            </InputGroup>
          )}
          
          <SubmitButton 
            type="submit"
            disabled={loading || isLoading}
          >
            {loading || isLoading ? '处理中...' : (isLoginMode ? '登录' : '注册')}
          </SubmitButton>
        </FormContainer>
        
        {isLoginMode && (
          <>
            <Divider>或</Divider>
            <WeChatButton 
              onClick={handleWechatLogin}
              disabled={loading || isLoading}
            >
              <WeChatIcon />
              微信登录
            </WeChatButton>
          </>
        )}
        
        <CloseButton onClick={handleClose}>
          取消
        </CloseButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default LoginModal;